import { TranslateService } from '@ngx-translate/core';
import { Company } from 'on-shared/_models/Company';
import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import swal from 'sweetalert2';
import { CompanyState } from 'on-shared/stores/company/company.state';
import { Observable, Subject } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { CompanyAction } from 'on-shared/stores/company/company.action';
import Swal from 'sweetalert2';
import { DomainName } from 'on-shared/_models/DomainName';
import { ReportsService } from 'on-shared/stores/reports/reports.service';
import { AccountingPlanState } from 'on-shared/stores/accounting-plan/accounting-plan.state';
import { AccountingPlanAction } from 'on-shared/stores/accounting-plan/accounting-plan.action';
import { AccountingPlan } from 'on-shared/_models/AccountingPlan';

export interface ReportNameTemplateVariable {
    code: string;
    label: string;
    example: string;
}

@Component({
    selector: 'on-settings-tab',
    templateUrl: './settings-tab.component.html',
    styleUrls: ['./settings-tab.component.scss'],
})
export class SettingsTabComponent implements OnInit, OnDestroy {
    isLoading: boolean;

    @ViewChild('templateInput') templateInput: ElementRef<HTMLInputElement>;
    private lastCursorPos: number = null;

    @Input() companyId: number;
    company: Company;

    useRate: boolean = false;
    rate: number;
    canEditRate: boolean = false;

    @Select(CompanyState.current) company$: Observable<Company>;
    @Select(CompanyState.domainNames) domainNames$: Observable<DomainName[]>;
    @Select(CompanyState.isLoadingDomainNames)
    isLoadingDomainNames$: Observable<boolean>;
    @Select(AccountingPlanState.userAccountingPlans)
    accountingPlans$: Observable<AccountingPlan[]>;

    destroy$ = new Subject<void>();

    templatePreview: string = '';
    isLoadingPreview: boolean = false;

    dateVariables: ReportNameTemplateVariable[] = [];

    userVariables: ReportNameTemplateVariable[] = [
        { code: '{USER_ID}', label: 'company.report-name-template.var-user-id', example: '' },
        { code: '{FIRST_NAME}', label: 'company.report-name-template.var-first-name', example: '' },
        { code: '{LAST_NAME}', label: 'company.report-name-template.var-last-name', example: '' },
        { code: '{INITIALS_FL}', label: 'company.report-name-template.var-initials-fl', example: '' },
        { code: '{INITIALS_LF}', label: 'company.report-name-template.var-initials-lf', example: '' },
        { code: '{ORGANIZATION_UNIT}', label: 'company.report-name-template.var-organization-unit', example: '' },
    ];

    infoVariables: ReportNameTemplateVariable[] = [
        { code: '{OTHER_INFO_1}', label: 'company.report-name-template.var-info-1', example: '' },
        { code: '{OTHER_INFO_2}', label: 'company.report-name-template.var-info-2', example: '' },
        { code: '{OTHER_INFO_3}', label: 'company.report-name-template.var-info-3', example: '' },
        { code: '{OTHER_INFO_4}', label: 'company.report-name-template.var-info-4', example: '' },
    ];

    accountingPlanVariables: ReportNameTemplateVariable[] = [];

    private static readonly MONTHS_FR = [
        'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
        'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
    ];

    constructor(
        private translateService: TranslateService,
        private store: Store,
        private reportsService: ReportsService,
    ) {
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const MM = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = String(now.getFullYear());
        const yy = yyyy.slice(2);
        const monthName = SettingsTabComponent.MONTHS_FR[now.getMonth()];

        this.dateVariables = [
            { code: '{DATE:dd/MM/yyyy}', label: 'dd/MM/yyyy', example: `${dd}/${MM}/${yyyy}` },
            { code: '{DATE:yyyyMMdd}', label: 'yyyyMMdd', example: `${yyyy}${MM}${dd}` },
            { code: '{DATE:MMMM yyyy}', label: 'MMMM yyyy', example: `${monthName} ${yyyy}` },
            { code: '{DATE:yyyy-MM}', label: 'yyyy-MM', example: `${yyyy}-${MM}` },
        ];
    }

    ngOnInit() {
        this.loadCompany();
        this.store.dispatch(new CompanyAction.LoadDomainNames());
        this.store.dispatch(new AccountingPlanAction.LoadAccountingPlans());

        this.accountingPlans$
            .pipe(takeUntil(this.destroy$))
            .subscribe((plans) => {
                if (plans) {
                    this.accountingPlanVariables = plans.map((plan) => ({
                        code: `{ANALYTICS:${plan.Name}}`,
                        label: plan.Name,
                        example: '',
                    }));
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadCompany() {
        this.isLoading = true;

        this.company$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
            this.company = new Company(response);

            this.useRate = !this.company.Settings.UseMileageScale;
            this.rate = this.company.Settings.RateAuto;
            this.canEditRate = this.useRate;

            this.isLoading = false;
        });
    }

    updateCompanyRights() {
        this.store.dispatch(
            new CompanyAction.SaveAuthorizationsChanges(this.company),
        );
    }

    updateCompanyRestaurantTicket() {
        this.store.dispatch(
            new CompanyAction.SaveRestaurantTicket(this.company),
        );
    }

    deleteCompany() {
        swal.fire({
            title: this.translateService.instant(
                'company.administration.swarl-title',
            ),
            text: this.translateService.instant(
                'company.administration.swarl-text',
            ),
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: this.translateService.instant(
                'company.administration.swarl-confirm-btn',
            ),
            cancelButtonText: this.translateService.instant(
                'company.administration.swarl-cancel-btn',
            ),
        }).then((result) => {
            if (result.value) {
                this.store.dispatch(new CompanyAction.Delete());
            }
        });
    }

    saveMileageSettings() {
        this.store.dispatch(
            new CompanyAction.UpdateMileageSettings(this.company),
        );
    }

    reload() {
        this.isLoading = true;
        this.store.dispatch(new CompanyAction.LoadCompanies());
        this.store.dispatch(new CompanyAction.LoadDomainNames());
    }

    requestNewDomainName() {
        Swal.fire({
            title: this.translateService.instant(
                'company.administration.request-dn-title',
            ),
            text: this.translateService.instant(
                'company.administration.request-dn-text',
            ),
            input: 'text',
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: this.translateService.instant(
                'company.administration.request-dn-text-confirm',
            ),
            cancelButtonText: this.translateService.instant(
                'company.administration.request-dn-text-cancel',
            ),
        }).then((domainEntered) => {
            if (domainEntered.value) {
                const dnRegex =
                    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/gi;

                const matches = dnRegex.exec(domainEntered.value);
                if (!matches || !matches.length) {
                    Swal.fire({
                        title: this.translateService.instant(
                            'company.administration.invalid-dn-title',
                        ),
                        text: this.translateService.instant(
                            'company.administration.invalid-dn-text',
                        ),
                    });
                    return;
                }

                this.store.dispatch(
                    new CompanyAction.RequestDomainName(domainEntered.value),
                );
            }
        });
    }

    leaveAccountantExpert() {
        Swal.fire({
            title: this.translateService.instant(
                'company.accountant-expert-leave-title',
            ),
            text: this.translateService.instant(
                'company.accountant-expert-leave-description',
            ),
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: this.translateService.instant(
                'company.accountant-expert-leave-ok',
            ),
            cancelButtonText: this.translateService.instant(
                'company.accountant-expert-leave-cancel',
            ),
            icon: 'warning',
        }).then((res) => {
            if (res.value) {
                this.store.dispatch(new CompanyAction.LeaveAccountantExpert());
            }
        });
    }

    onTemplateBlur(event: FocusEvent) {
        const input = event.target as HTMLInputElement;
        this.lastCursorPos = input.selectionStart;
        this.previewTemplate();
    }

    insertVariable(variable: ReportNameTemplateVariable) {
        const value = this.company.Settings.ReportNameTemplate || '';
        const input = this.templateInput?.nativeElement;
        const start = input ? input.selectionStart ?? this.lastCursorPos ?? value.length : this.lastCursorPos ?? value.length;
        const end = input ? input.selectionEnd ?? start : start;

        this.company.Settings.ReportNameTemplate =
            value.substring(0, start) + variable.code + value.substring(end);

        this.previewTemplate();

        // Reposition cursor after inserted variable
        const newPos = start + variable.code.length;
        setTimeout(() => {
            if (input) {
                input.focus();
                input.setSelectionRange(newPos, newPos);
            }
        });
    }

    saveReportNameTemplate() {
        this.store.dispatch(
            new CompanyAction.SaveReportNameTemplate(this.company),
        );
    }

    previewTemplate() {
        if (!this.company.Settings.ReportNameTemplate) {
            this.templatePreview = '';
            return;
        }
        this.isLoadingPreview = true;
        this.reportsService
            .PreviewReportNameTemplate(
                this.company.Settings.ReportNameTemplate,
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.isLoadingPreview = false;
                    if (response.IsSuccess) {
                        this.templatePreview = response.Result;
                    } else {
                        this.templatePreview = this.translateService.instant(
                            'company.report-name-template.preview-error',
                        );
                    }
                },
                () => {
                    this.isLoadingPreview = false;
                    this.templatePreview = this.translateService.instant(
                        'company.report-name-template.preview-error',
                    );
                },
            );
    }
}
